import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { format } from "date-fns";

import { faClock, faList } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components/Icon/Icon";

import { AssignStatus } from "@/enum/list.enum";

import { ListPersonStatus } from "@/pages/Private/pages/List/components/ListPersonStatus";

import { ListAppearanceEntry } from "../../../schema/trackerSignalResult";

interface ListAppearanceCardProps {
	appearance: ListAppearanceEntry;
	/** When the appearance happened; the timeline is ordered by it. */
	occurredAt?: string | null;
}

/** "12 May 2026", matching the date format the "Also on these lists" block already uses. */
const formatAppearanceDate = (iso?: string | null): string => {
	if (!iso) {
		return "";
	}

	const date = new Date(iso);

	return isNaN(date.getTime()) ? "" : format(date, "d MMM yyyy");
};

/**
 * A lead — or, on a company timeline, a colleague from the same company — turning up on a lead
 * list, rendered as a timeline entry beside the AI signals (PREC-2496).
 *
 * Display only, like every other card in this timeline: the navigable version of the same fact is
 * the "Also on these lists" block on the Overview tab.
 */
export const ListAppearanceCard: FunctionComponent<ListAppearanceCardProps> = ({
	appearance,
	occurredAt,
}) => {
	const { t } = useTranslation();

	const date = formatAppearanceDate(occurredAt);
	const who = appearance.isSelf
		? t("profileTracker.signalTimeline.appearedOnList")
		: t("profileTracker.signalTimeline.colleagueAppearedOnList", {
				name: appearance.prospectName ?? "",
			});

	return (
		<div className="flex w-full flex-col overflow-hidden rounded-lg border border-gray-200 bg-white p-4">
			<div className="flex items-start gap-2">
				<Icon className="mt-0.5 shrink-0 text-xs text-text-tertiary" icon={faList} />

				<div className="min-w-0 flex-1">
					<p className="truncate text-sm font-semibold text-text">{appearance.listName}</p>
					<p className="mt-0.5 text-xs text-text-tertiary">{who}</p>
					{!appearance.isSelf && appearance.prospectTitle && (
						<p className="mt-0.5 truncate text-xs text-text-tertiary">{appearance.prospectTitle}</p>
					)}
				</div>

				<div className="flex shrink-0 flex-col items-end gap-1">
					{date && (
						<span className="flex items-center gap-1 text-xxs text-text-tertiary">
							<Icon className="text-[10px]" icon={faClock} />
							{date}
						</span>
					)}
					{appearance.assignStatus && (
						<ListPersonStatus size="sm" status={appearance.assignStatus as AssignStatus} />
					)}
				</div>
			</div>
		</div>
	);
};
